import { ReactNode, memo, ChangeEvent } from 'react'
import { AccordionProps } from '@material-ui/core'
import Typography from '@material-ui/core/Typography'
import AccordionDetails from '@material-ui/core/AccordionDetails'
import AccordionSummary from '@material-ui/core/AccordionSummary'
import MuiAccordion from '@material-ui/core/Accordion'
import { makeStyles, Theme } from '@material-ui/core/styles'
import ArrowDropDownIcon from '@material-ui/icons/ArrowDropDown'
import KeyboardArrowDownIcon from '@material-ui/icons/KeyboardArrowDown'
import CloseIcon from '@material-ui/icons/Close'
import { TransitionProps } from '@material-ui/core/transitions'
import Grow from '@material-ui/core/Grow'
import { Variant } from '@material-ui/core/styles/createTypography'

import Markdown from 'components/Markdown'

interface OwnProps {
  title: ReactNode
  isClosable?: boolean
  isExpanded: boolean
  handleChange: (_: ChangeEvent<unknown>, didExpand: boolean) => void
  handleClose: () => void

  // styling props:
  accordionBackgroundColor: string
  titleVariant?: Variant | 'inherit'
  titleColor?: 'primary'
  titleFontSize?: string
  titleFontSizeDesktop?: string
  detailsTitleVariant?: Variant | 'inherit'
  lineHeight?: string
  withBoxShadow?: boolean
  withBoldTitle?: boolean
  withSlimExpandIcon?: boolean
  withNoPadding?: boolean
}

type IntroductionProps = Omit<AccordionProps, 'title'> & OwnProps

const defaultTransitionProps: TransitionProps = { unmountOnExit: true }

const useStyles = makeStyles<
  Theme,
  {
    accordionBackgroundColor: string
    lineHeight: string
    withBoxShadow: boolean
    withNoPadding: boolean
    titleFontSize: string | undefined
    titleFontSizeDesktop: string | undefined
    isClosable: boolean | undefined
  }
>((theme) => ({
  accordion: {
    marginTop: ({ withNoPadding }) =>
      withNoPadding ? 0 : `${theme.spacing(4)}px !important`,
    marginBottom: ({ withNoPadding }) =>
      withNoPadding ? 0 : `${theme.spacing(4)}px !important`,
    border: 'none',
    backgroundColor: ({ accordionBackgroundColor }) => accordionBackgroundColor,
    borderRadius: theme.shape.borderRadius,
    boxShadow: 'none',
    transition: theme.transitions.create('box-shadow', { duration: 300 }),
    '&::before': {
      opacity: 0
    }
  },
  accordionExpanded: {
    boxShadow: ({ withBoxShadow }) =>
      withBoxShadow ? theme.shadows[4] : 'none'
  },
  container: {
    position: 'relative'
  },
  close: {
    position: 'absolute',
    top: theme.spacing(0.5),
    right: theme.spacing(0.5),
    cursor: 'pointer'
  },
  titleClassName: {
    fontSize: ({ titleFontSize }) => titleFontSize || '1.125rem',
    lineHeight: '1.5rem',

    [theme.breakpoints.up('sm')]: {
      fontSize: ({ titleFontSizeDesktop }) => titleFontSizeDesktop || '1.125rem'
    }
  },
  boldTitle: {
    fontWeight: 'bold'
  },
  accordionDetails: {
    padding: ({ withNoPadding }) => (withNoPadding ? 0 : undefined)
  },
  summary: {
    justifyContent: 'flex-start',
    padding: ({ withNoPadding }) => (withNoPadding ? 0 : undefined),
    minHeight: ({ withNoPadding }) =>
      withNoPadding ? '0 !important' : undefined,
    flexGrow: ({ withNoPadding }) => (withNoPadding ? 0 : 1),
    margin: ({ withNoPadding }) => (withNoPadding ? '0 !important' : undefined),

    '&.Mui-expanded': {
      margin: ({ withNoPadding }) =>
        withNoPadding ? '0 !important' : undefined
    }
  },
  expandIcon: {
    marginRight: ({ isClosable }) => (isClosable ? theme.spacing(2) : undefined)
  },
  body: {
    '&>div': {
      '&> *:first-child': {
        marginTop: 0
      },
      '&>h3': {
        fontSize: '1.0625rem',
        lineHeight: ({ lineHeight }) => lineHeight
      }
    }
  }
}))

const IntroductionStyleComponent = memo(
  ({
    title,
    children,
    isClosable = false,
    handleChange,
    handleClose,
    isExpanded,

    // styling props:
    accordionBackgroundColor,
    titleVariant = 'inherit',
    titleFontSize,
    titleFontSizeDesktop,
    detailsTitleVariant = 'inherit',
    lineHeight = 'inherit',
    titleColor,
    withBoxShadow = false,
    withBoldTitle = false,
    withSlimExpandIcon = false,
    withNoPadding = false
  }: IntroductionProps) => {
    const {
      container,
      titleClassName,
      body,
      close,
      boldTitle,
      accordionDetails,
      summary,
      accordion,
      accordionExpanded,
      expandIcon
    } = useStyles({
      accordionBackgroundColor,
      lineHeight,
      withBoxShadow,
      titleFontSize,
      titleFontSizeDesktop,
      withNoPadding,
      isClosable
    })

    const expandIconProps = {
      className: expandIcon,
      color: 'primary' as const
    }

    const ExpandIcon = withSlimExpandIcon ? (
      <KeyboardArrowDownIcon {...expandIconProps} />
    ) : (
      <ArrowDropDownIcon {...expandIconProps} />
    )

    return (
      <Grow in>
        <div className={container}>
          <MuiAccordion
            className={`${accordion} ${isExpanded ? accordionExpanded : ''}`}
            TransitionProps={defaultTransitionProps}
            expanded={isExpanded}
            onChange={handleChange}
          >
            <AccordionSummary
              className={summary}
              expandIcon={ExpandIcon}
              aria-controls="Introduction-content"
            >
              <div>
                {typeof title === 'string' ? (
                  <Typography
                    className={`${titleClassName} ${
                      withBoldTitle ? boldTitle : ''
                    }`}
                    variant={titleVariant}
                    {...(titleColor && { color: titleColor })}
                  >
                    {title}
                  </Typography>
                ) : (
                  title
                )}
              </div>
            </AccordionSummary>
            <AccordionDetails className={accordionDetails}>
              <Typography
                className={body}
                variant={detailsTitleVariant}
                component="div"
              >
                <Markdown>{children}</Markdown>
              </Typography>
            </AccordionDetails>
          </MuiAccordion>
          {isClosable && (
            <CloseIcon
              className={close}
              color="disabled"
              fontSize="small"
              aria-label="close"
              onClick={handleClose}
            />
          )}
        </div>
      </Grow>
    )
  }
)

export { IntroductionStyleComponent }
